import { TC_STALE_AFTER_HOURS } from "../theme/tokens";

/**
 * «Jammet 06:12, 9 t siden».
 *
 * ORDET ER «JAMMET», og det er en omgjoering. Dokumentene forbød det
 * tidligere, fordi jam teknisk betyr aa synkronisere mot en ekstern kilde —
 * og appen gjoer ikke det. Men det er ordet som brukes paa sett, og
 * grensesnittet skal snakke som bransjen gjoer. Avgjort 08.10.2026; se
 * docs/04-timecode.md og CLAUDE.md.
 *
 * I kode og datamodell heter handlingen fortsatt tidskodesetting — `tc_anchor`,
 * `adjustAnchorTo` — fordi det er det den er.
 *
 * Ligger for seg selv fordi teksten staar i menyen, under produksjonen. Den
 * sto foer under tidskoden i toppstripa, men ble flyttet — se TopBar.
 *
 * `gammel` er sann naar ankeret er eldre enn TC_STALE_AFTER_HOURS. Da har
 * tidskoden staatt paa egen klokke saa lenge at regi boer vite det.
 */
/**
 * Tidskodens tilstand.
 *
 * `ujammet` — ingen har jammet. Tidskoden er ikke satt for produksjonen,
 *             og flata viser STREKER. Appen gaar paa sin egen klokke
 *             innvendig, slik at poster kan skrives, men den klokka er
 *             ikke en tidskode noen har avtalt.
 * `gammel`   — siste jam er eldre enn TC_STALE_AFTER_HOURS. Roed.
 * `jammet`   — satt av en tekniker innenfor fristen.
 */
export type Tidskodetilstand = "ujammet" | "gammel" | "jammet";

export interface JamInfo {
  readonly tekst: string;
  readonly tilstand: Tidskodetilstand;
  /** Samme som `tilstand === "gammel"`. Menyen demper teksten paa den. */
  readonly gammel: boolean;
}

const p2 = (n: number): string => String(n).padStart(2, "0");

/**
 * Hva staar det under produksjonen, og hvilken tilstand er tidskoden i?
 *
 * TAR ANKERET, IKKE BARE ET TIDSPUNKT. Den gamle utgaven tok
 * `anchor.wallUtcMs` og skrev «Jammet 11:30» uansett hva slags anker det
 * var — ogsaa for appens eget klokkeanker, som ingen har jammet. Brukeren
 * 01.11.2026: «Jeg ser ogsaa at appen jammer selv naar jeg aapner den.»
 * Det gjorde den ikke — den skrev `kind: "klokke"` som den skulle — men
 * flata sa at den gjorde det, og da er det flata som lyver.
 *
 * Alderen regnes fra `jammetUtcMs` og ikke fra `wallUtcMs`: paa et
 * adoptert anker er det siste naar VI adopterte. Se StoredAnchor.
 */
export function jamInfo(
  anker: {
    readonly kind: "jam" | "klokke" | "avledet";
    readonly jammetUtcMs: number | null;
  } | null,
  wallUtcMs: number,
): JamInfo {
  const jammet = anker === null || anker.kind === "klokke"
    ? null : anker.jammetUtcMs;

  if (jammet === null) {
    return {
      tekst: "Ikke jammet. Tidskoden er ikke satt for dagen.",
      tilstand: "ujammet",
      gammel: false,
    };
  }

  const timer = Math.floor((wallUtcMs - jammet) / 3_600_000);
  const set = new Date(jammet);
  const gammel = timer >= TC_STALE_AFTER_HOURS;
  return {
    tekst: `Jammet ${p2(set.getHours())}:${p2(set.getMinutes())}, ${timer} t siden`,
    tilstand: gammel ? "gammel" : "jammet",
    gammel,
  };
}
